"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Clock, ShieldCheck, Award, FileCheck2, ArrowRight, PhoneCall, CircleCheck as CheckCircle2, ChevronRight } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
const badges = [{
  icon: Clock,
  title: "Technical team review",
  subtitle: "Application fit discussion"
}, {
  icon: ShieldCheck,
  title: "Application Expert Review",
  subtitle: "Quotation follow-up by Antech"
}, {
  icon: Award,
  title: "Authorised Distributor",
  subtitle: "Direct manufacturer backing"
}];
const features = ["Itemised pricing & system configuration", "Method feasibility assessment options", "IQ/OQ qualification documentation support", "Applicable across Ireland & UK facilities"];
export default function QuotationHero() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        staggerChildren: shouldReduceMotion ? 0 : 0.12,
        ease: "easeOut"
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 15
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="quotationhero" data-nav="dark" className="relative w-full overflow-hidden bg-sidebar text-sidebar-foreground py-16 md:py-24 border-b border-sidebar-border">
      {/* Subtle background decorative grid/glow */}
      <div className="pointer-events-none absolute inset-0 opacity-10" aria-hidden="true">
        <div className="absolute -top-32 -left-32 w-96 h-96 rounded-full bg-primary/30 blur-3xl" />
        <div className="absolute -bottom-32 -right-32 w-96 h-96 rounded-full bg-chart-3/20 blur-3xl" />
      </div>

      <div className="page-container relative z-10 max-w-5xl mx-auto flex flex-col items-center text-center">
        <motion.div variants={containerVariants} initial="hidden" animate="visible" className="flex flex-col items-center w-full">
          {/* Breadcrumb navigation */}
          <motion.nav variants={itemVariants} aria-label="Breadcrumb" className="mb-6">
            <ol className="flex flex-wrap items-center justify-center gap-1.5 text-xs sm:text-sm text-sidebar-foreground/70">
              <li className="inline-flex items-center gap-1.5">
                <Link to="/HomePage" className="transition-colors hover:text-primary">
                  Home
                </Link>
              </li>
              <li aria-hidden="true" className="opacity-50">
                <ChevronRight className="size-3.5" />
              </li>
              <li className="inline-flex items-center gap-1.5">
                <span className="font-medium text-sidebar-foreground" aria-current="page">
                  Request a Quote
                </span>
              </li>
            </ol>
          </motion.nav>

          {/* Top category chip */}
          <motion.div variants={itemVariants} className="mb-4">
            <Badge variant="outline" className="border-primary/40 bg-sidebar-accent text-primary px-3.5 py-1 text-xs md:text-sm font-medium tracking-wide uppercase rounded-full shadow-xs gap-1.5">
              <FileCheck2 className="size-3.5 text-primary" />
              Direct Technical Procurement
            </Badge>
          </motion.div>

          {/* Main heading */}
          <motion.h1 variants={itemVariants} className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-default font-bold text-sidebar-foreground tracking-tight max-w-4xl leading-tight md:leading-tight">
            Request a Formal <span className="text-primary">Analytical Instrument</span> Quotation
          </motion.h1>

          {/* Subheading / Lead copy */}
          <motion.p variants={itemVariants} className="mt-6 text-base sm:text-lg md:text-xl text-sidebar-foreground/80 max-w-2xl font-normal leading-relaxed">
            Receive complete technical specifications, custom method compatibility, and official pricing for regulated laboratories and manufacturing teams across Ireland and the UK.
          </motion.p>

          {/* CTAs */}
          <motion.div variants={itemVariants} className="mt-8 flex flex-col sm:flex-row items-center gap-4 w-full sm:w-auto">
            <Button size="lg" className="w-full sm:w-auto bg-primary text-primary-foreground hover:bg-primary/90 font-medium px-8 py-3 text-base shadow-md h-12 rounded-md" asChild>
              <Link to="/ContactPage" className="inline-flex items-center justify-center gap-2">
                <span>Start Quote Request</span>
                <ArrowRight className="size-4" />
              </Link>
            </Button>

            <Button variant="outline" size="lg" className="w-full sm:w-auto border-sidebar-border bg-sidebar-accent/50 text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-foreground px-6 py-3 text-base h-12 rounded-md" asChild>
              <Link to="/DiscussYourApplicationPage" className="inline-flex items-center justify-center gap-2">
                <PhoneCall className="size-4 text-primary" />
                <span>Discuss Your Application</span>
              </Link>
            </Button>
          </motion.div>

          {/* Key assurance bullet points */}
          <motion.div variants={itemVariants} className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-y-2 gap-x-6 text-left max-w-2xl w-full">
            {features.map((feature, idx) => <div key={idx} data-index={idx} className="flex items-center gap-2 text-xs sm:text-sm text-sidebar-foreground/85">
                <CheckCircle2 className="size-4 text-primary shrink-0" />
                <span>{feature}</span>
              </div>)}
          </motion.div>

          {/* SLA & Response-time Badges Grid */}
          <motion.div variants={itemVariants} className="mt-12 w-full pt-10 border-t border-sidebar-border/60 grid grid-cols-1 md:grid-cols-3 gap-4 lg:gap-6">
            {badges.map((badge, index) => {
            const IconComponent = badge.icon;
            return <div key={index} data-index={index} className="flex items-center gap-4 p-4 rounded-lg bg-sidebar-accent/40 border border-sidebar-border text-left backdrop-blur-xs transition-[border-color,background-color] hover:border-primary/50 hover:bg-sidebar-accent/70">
                  <div className="p-2.5 rounded-md bg-primary/15 text-primary shrink-0">
                    <IconComponent className="size-5" />
                  </div>
                  <div className="flex flex-col min-w-0">
                    <span className="text-sm font-semibold text-sidebar-foreground truncate">
                      {badge.title}
                    </span>
                    <span className="text-xs text-sidebar-foreground/70">
                      {badge.subtitle}
                    </span>
                  </div>
                </div>;
          })}
          </motion.div>
        </motion.div>
      </div>
    </section>;
}